import { createContext, ReactNode, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

type FiltersMap = Record<string, any>;

type FilterContextType = {
  getFilters: <T>(key: string, initFilters: T) => [T, (filters: T) => void];
};

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const FilterProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [filtersMap, setFiltersMap] = useState<FiltersMap>({});

  useEffect(() => {
    Object.keys(filtersMap).forEach(key => {
      if (!location.pathname.startsWith(key)) {
        // remove key from state and localStorage
        const newMap = { ...filtersMap };
        delete newMap[key];
        setFiltersMap(newMap);
        localStorage.removeItem(`filters-${key}`);
      }
    });
  }, [location.pathname]);

  const getFilters = <T,>(basePath: string, initFilters: T): [T, (filters: T) => void] => {
    const currentFilters = filtersMap[basePath];

    const setFilters = (newFilters: T) => {
      setFiltersMap(prev => ({
        ...prev,
        [basePath]: newFilters
      }));
      localStorage.setItem(`filters-${basePath}`, JSON.stringify(newFilters));
    };
    return [currentFilters ?? initFilters, setFilters];
  };

  return (
    <FilterContext.Provider value={{ getFilters }}>
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) throw new Error("useFilterContext must be used within a FilterProvider");
  return context;
};